import type { ReleaseBulkAction, ReleaseWithMetrics } from '@planuze/admin-contracts';
import { DESTRUCTIVE_RELEASE_BULK_ACTIONS } from '@planuze/admin-contracts';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  InlineAlert,
  Input,
  Label,
  Textarea,
} from '@planuze/ui';
import { useEffect, useState } from 'react';
import {
  type ReleasesAdminContent,
  useReleasesAdminContent,
} from '../../content/sections/releases-admin.js';

/* Confirmação das ações da tela de versões (ADR-0809/0825).
   A remoção perde índice e métricas, embora preserve o artefato publicado; por isso continua em
   Dialog. Toda ação em lote diz QUANTOS itens vai afetar antes de liberar o botão — a barra de lote
   opera sobre uma seleção que pode ter rolado para fora da tela. */

export type ReleasePending =
  | { readonly kind: 'delete'; readonly release: ReleaseWithMetrics }
  | { readonly kind: 'purge'; readonly release: ReleaseWithMetrics }
  | {
      readonly kind: 'bulk';
      readonly ids: readonly string[];
      readonly action: ReleaseBulkAction;
    };

export interface ReleasePurgeConfirmation {
  readonly confirmation: string;
  readonly reason: string;
}

function describe(
  pending: ReleasePending,
  copy: ReleasesAdminContent['confirmDialog'],
): { title: string; body: string } {
  if (pending.kind === 'delete') {
    const { version, platform, arch, kind } = pending.release;
    return {
      title: copy.deleteTitle,
      body: copy.deleteBody(version, platform, arch, kind),
    };
  }
  if (pending.kind === 'purge') {
    const { channel, version } = pending.release;
    return {
      title: copy.purgeTitle,
      body: copy.purgeBody(channel, version),
    };
  }
  return {
    title: copy.bulkTitles[pending.action],
    body: copy.bulkBody(pending.ids.length, copy.bulkEffects[pending.action]),
  };
}

export function ReleaseConfirmDialog({
  pending,
  busy,
  onCancel,
  onConfirm,
}: {
  readonly pending: ReleasePending | null;
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onConfirm: (purge?: ReleasePurgeConfirmation) => void;
}) {
  const copy = useReleasesAdminContent().confirmDialog;
  const [confirmation, setConfirmation] = useState('');
  const [reason, setReason] = useState('');
  const purgeTarget =
    pending?.kind === 'purge' ? `${pending.release.channel}/${pending.release.version}` : null;
  useEffect(() => {
    if (purgeTarget === null) return;
    setConfirmation('');
    setReason('');
  }, [purgeTarget]);
  const destructive =
    pending !== null &&
    (pending.kind === 'delete' ||
      pending.kind === 'purge' ||
      DESTRUCTIVE_RELEASE_BULK_ACTIONS.includes(pending.action));
  const text = pending === null ? null : describe(pending, copy);
  const purgeReady =
    pending?.kind !== 'purge' || (confirmation === purgeTarget && reason.trim().length >= 3);
  return (
    <Dialog
      open={pending !== null}
      onOpenChange={(open) => {
        if (!open && !busy) onCancel();
      }}
    >
      {/* `locked` enquanto a mutation está em voo: fechar no meio de um lote deixaria o staff sem
          saber quantos itens chegaram a ser aplicados (ADR-0216). */}
      <DialogContent locked={busy}>
        <DialogHeader>
          <DialogTitle className={destructive ? 'text-destructive' : undefined}>
            {text?.title ?? ''}
          </DialogTitle>
          <DialogDescription>{text?.body ?? ''}</DialogDescription>
        </DialogHeader>
        {pending?.kind === 'purge' && purgeTarget !== null && (
          <div className="space-y-4">
            <InlineAlert variant="destructive">{copy.purgeWarning}</InlineAlert>
            <div className="space-y-1.5">
              <Label htmlFor="release-purge-confirmation">
                {copy.purgeConfirmationLabel(purgeTarget)}
              </Label>
              <Input
                id="release-purge-confirmation"
                autoComplete="off"
                maxLength={100}
                value={confirmation}
                placeholder={purgeTarget}
                onChange={(event) => setConfirmation(event.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="release-purge-reason">{copy.purgeReasonLabel}</Label>
              <Textarea
                id="release-purge-reason"
                rows={3}
                minLength={3}
                maxLength={500}
                value={reason}
                placeholder={copy.purgeReasonPlaceholder}
                onChange={(event) => setReason(event.target.value)}
              />
            </div>
          </div>
        )}
        {destructive && pending?.kind !== 'purge' && (
          <InlineAlert variant="destructive">
            {copy.irreversibleWarningPrefix} <strong>{copy.irreversibleWarningStrong}</strong>{' '}
            {copy.irreversibleWarningSuffix}
          </InlineAlert>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onCancel} disabled={busy}>
            {copy.cancel}
          </Button>
          <Button
            variant={destructive ? 'destructive' : 'default'}
            loading={busy}
            disabled={!purgeReady}
            onClick={() =>
              pending?.kind === 'purge'
                ? onConfirm({ confirmation, reason: reason.trim() })
                : onConfirm()
            }
          >
            {pending?.kind === 'purge' ? copy.confirmPurge : copy.confirm}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
